import { FormEvent, useState } from 'react';
import { userSignIn } from '../lib/auth';

type Props = {
  onSuccess: () => void;
};

export function LoginPage({ onSuccess }: Props) {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.includes('@') || password.length < 6) {
      setError('Enter a valid email and a password with at least 6 characters.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await userSignIn(email.trim(), password);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page login-page--customer">
      <section className="login-visual login-visual--customer" aria-label="Welcome">
        <div className="brand login-brand" aria-label="Igentify Pulse">
          <span className="brand-wave" aria-hidden="true">
            <i /><i /><i /><i /><i /><i /><i />
          </span>
          <span className="brand-name">Igentify Pulse</span>
        </div>
        <div className="login-copy">
          <div className="eyebrow">Your conversation space</div>
          <h1>
            Talk with your
            <br />
            <span>AI partner.</span>
          </h1>
          <p>
            Start a focused voice and video session. Ask questions, explore ideas, and leave with a clear next step.
          </p>
          <div className="login-feature"><i>✓</i>Natural voice conversation</div>
          <div className="login-feature"><i>✓</i>Camera preview when you want it</div>
          <div className="login-feature"><i>✓</i>Private, signed-in session</div>
        </div>
        <div className="login-art" aria-hidden="true">
          <span className="beam" />
          <div className="pulse">
            {Array.from({ length: 41 }).map((_, i) => (
              <i key={i} style={{ height: 16 + Math.abs(Math.sin(i * 0.64) * 80) }} />
            ))}
          </div>
        </div>
        <div className="login-footer">
          <span>© 2026 Igentify Pulse</span>
          <span>Privacy · Security</span>
        </div>
      </section>

      <section className="login-form-side">
        <form className="login-panel" onSubmit={(e) => void onSubmit(e)}>
          <div className="eyebrow">Customer sign-in</div>
          <h2>Welcome</h2>
          <p>Sign in to begin your conversation with your digital assistant.</p>
          <label className="login-field">
            Email
            <div className="login-input-wrap">
              <input
                aria-label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@company.com"
                autoComplete="username"
                required
              />
            </div>
          </label>
          <label className="login-field">
            Password
            <div className="login-input-wrap">
              <input
                aria-label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={show ? 'text' : 'password'}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide password' : 'Show password'}>
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>
          {error ? <div className="login-error" role="alert">{error}</div> : null}
          <button className="login-submit" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Start conversation'}
          </button>
        </form>
      </section>
    </main>
  );
}
